// standards-ignore: DRY:string — 'Assign to me' is a translation key reference, not a duplicated literal
import { useCallback, useMemo } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { IconPlusSmall } from '@amzn/stencil-react-icons';
import { MAX_ASSIGNEES } from '../../../shared/domain';
import { useMessages } from '../../../shared/i18n/useMessages';
import { MultiAssigneePicker } from '../../../shared/ui/MultiAssigneePicker';

const MODALS_BUNDLE = 'features.audit.translations.modals';

type EditorKey =
  | 'assigneesAssignToMe'
  | 'assigneesInputPlaceholder'
  | 'assigneesAddLabel'
  | 'assigneesErrorRequired'
  | 'assigneesErrorInvalidAlias'
  | 'assigneesErrorDuplicate'
  | 'assigneesErrorMaxReached';

export interface AssigneesEditorProps {
  /** Current list of assignee aliases. */
  readonly assignees: readonly string[];
  /** Called when a new assignee should be added. */
  readonly onAdd: (alias: string) => void;
  /** Called when an assignee should be removed. */
  readonly onRemove: (alias: string) => void;
  /** Program scope for the employee-search suggest call. */
  readonly programId: string;
  /** Current user's alias — powers "Assign to me" button. Omit to hide it. */
  readonly currentUserAlias?: string;
  /** Whether the editor is disabled (e.g. during a save). */
  readonly disabled?: boolean;
  /**
   * Compact mode hides the "Assign to me" button — use in create
   * forms where only the picker + chips are needed.
   */
  readonly compact?: boolean;
}

/**
 * Assignee editor block used by ManageAssigneesModal and
 * CreateAuditSheet.
 *
 * Wraps `MultiAssigneePicker` (shared/ui) and optionally adds the
 * "Assign to me" button for manage-assignees contexts.
 */
export function AssigneesEditor({
  assignees,
  onAdd,
  onRemove,
  programId,
  currentUserAlias,
  disabled = false,
  compact = false,
}: AssigneesEditorProps): JSX.Element {
  const { t, f } = useMessages<EditorKey>(MODALS_BUNDLE);

  const errorMessages = useMemo(
    () => ({
      required: t('assigneesErrorRequired'),
      invalidAlias: t('assigneesErrorInvalidAlias'),
      duplicate: t('assigneesErrorDuplicate'),
      maxReached: f('assigneesErrorMaxReached', { max: MAX_ASSIGNEES }),
    }),
    [t, f],
  );

  const isCurrentUserAssigned =
    currentUserAlias != null && assignees.includes(currentUserAlias);
  const isAtMax = assignees.length >= MAX_ASSIGNEES;

  const handleAssignToMe = useCallback(() => {
    if (currentUserAlias) onAdd(currentUserAlias.trim().toLowerCase());
  }, [currentUserAlias, onAdd]);

  return (
    <Col gridGap="dimensions.spacing.300">
      {/* Assign to me — hidden in compact mode or when at max capacity */}
      {!compact && currentUserAlias && !isCurrentUserAssigned && !isAtMax && (
        <Button
          variant={ButtonVariant.Secondary}
          icon={<IconPlusSmall />}
          onClick={handleAssignToMe}
          disabled={disabled}
        >
          {t('assigneesAssignToMe')}
        </Button>
      )}

      <MultiAssigneePicker
        assignees={assignees}
        onAdd={onAdd}
        onRemove={onRemove}
        programId={programId}
        disabled={disabled}
        placeholder={t('assigneesInputPlaceholder')}
        ariaLabel={t('assigneesAddLabel')}
        dataTestId="assignees-employee-picker"
        errorMessages={errorMessages}
      />
    </Col>
  );
}
